// Kullanıcılar — paylaşılan UI bileşenleriyle yeniden tasarlandı.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import DataTable from '../components/DataTable';
import { TabIcon, TAB_ICONS } from '../utils/tabIcons';
import { Button, Modal, FormField, ListPageHeader, FilterBar } from '../components/ui';
import { CardHero, CardStatusBadge, CardTabs, CardSplitButton } from '../components/card';

const emptyUser = {
  username: '', email: '', full_name: '', phone: '', image_url: null,
  role: 'user', is_active: true, is_blocked: false, role_ids: [],
  personnel_id: '', default_cash_register_id: '', default_warehouse_id: '', default_branch_id: '',
  is_cashier: false,
  retail_allow_credit_card: true, retail_allow_bank_transfer: true,
  retail_allow_voucher: true, retail_allow_on_account: true,
  retail_allow_price_edit: true,
  period_ids: [],
  visible_warehouse_ids: [], // boş = HEPSİ görünür; dolu = sadece bu depolar
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('tr-TR');
}

export default function UsersPage() {
  const { user: me, hasPermission } = useAuth();
  const canCreate = hasPermission('users.create');
  const canEdit   = hasPermission('users.edit');
  const canDelete = hasPermission('users.delete');

  const [list, setList] = useState([]);
  const [roles, setRoles] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [cashRegisters, setCashRegisters] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [filters, setFilters] = useState({ role: '', is_active: '', is_blocked: '' });

  const [modalMode, setModalMode] = useState(null); // 'add' | 'edit'
  const [activeTab, setActiveTab] = useState('general');
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ ...emptyUser });
  const [password, setPassword] = useState('');
  const [pwDirty, setPwDirty] = useState(false);
  const [imgUploading, setImgUploading] = useState(false);
  const imgInputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const [u, r, p, cr, w, fp, br] = await Promise.all([
        api.get('/users', { params }),
        api.get('/roles'),
        api.get('/personnel', { params: { limit: 500 } }),
        api.get('/cash-registers'),
        api.get('/warehouses?bypass_scope=1'),
        api.get('/fiscal-periods'),
        api.get('/branches', { params: { is_active: 'yes' } }).catch(() => ({ data: { success: false } })),
      ]);
      if (u.data.success) setList(u.data.data || []);
      if (r.data.success) setRoles(r.data.data || []);
      if (p.data.success) setPersonnel((p.data.data.items || []).filter(x => x.is_active));
      if (cr.data.success) setCashRegisters((cr.data.data.items || cr.data.data || []).filter(x => x.is_active !== false));
      if (w.data.success) setWarehouses((w.data.data.items || w.data.data || []).filter(x => x.is_active !== false));
      if (fp.data.success) setPeriods((fp.data.data || []).filter(x => x.is_active !== false));
      if (br.data.success) setBranches(br.data.data.items || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openAdd = () => {
    setForm({ ...emptyUser });
    setPassword('');
    setPwDirty(false);
    setSelected(null);
    setMsg(null);
    setActiveTab('general');
    setModalMode('add');
  };

  const openEdit = async (u) => {
    try {
      const r = await api.get('/users/' + u.id);
      if (r.data.success) {
        const d = r.data.data;
        setForm({
          username: d.username || '',
          email: d.email || '',
          full_name: d.full_name || '',
          phone: d.phone || '',
          image_url: d.image_url || null,
          role: d.role || 'user',
          is_active: d.is_active !== false,
          is_blocked: !!d.is_blocked,
          role_ids: (d.roles || []).map(r => r.id),
          personnel_id: d.personnel_id || '',
          default_cash_register_id: d.default_cash_register_id || '',
          default_warehouse_id: d.default_warehouse_id || '',
          default_branch_id: d.default_branch_id || '',
          is_cashier: !!d.is_cashier,
          retail_allow_credit_card:   !!d.retail_allow_credit_card,
          retail_allow_bank_transfer: !!d.retail_allow_bank_transfer,
          retail_allow_voucher:       !!d.retail_allow_voucher,
          retail_allow_on_account:    !!d.retail_allow_on_account,
          retail_allow_price_edit:    !!d.retail_allow_price_edit,
          period_ids: (d.allowed_periods || []).map(p => p.id),
          visible_warehouse_ids: (() => {
            const raw = d.visible_warehouse_ids;
            if (!raw) return [];
            try { const arr = JSON.parse(raw); return Array.isArray(arr) ? arr : []; }
            catch { return []; }
          })(),
        });
        setPassword('');
        setPwDirty(false);
        setSelected(d);
        setMsg(null);
        setActiveTab('general');
        setModalMode('edit');
      }
    } catch (e) { console.error(e); }
  };

  const closeModal = () => { setModalMode(null); setSelected(null); setMsg(null); };
  const tf = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const toggleRole = (rid) => setForm(prev => ({
    ...prev,
    role_ids: prev.role_ids.includes(rid) ? prev.role_ids.filter(x => x !== rid) : [...prev.role_ids, rid],
  }));
  const togglePeriod = (pid) => setForm(prev => ({
    ...prev,
    period_ids: prev.period_ids.includes(pid) ? prev.period_ids.filter(x => x !== pid) : [...prev.period_ids, pid],
  }));

  const handleSave = async (mode = 'close') => {
    if (mode && typeof mode === 'object') mode = 'close';
    if (!form.username.trim() || !form.email.trim() || !form.full_name.trim())
      return setMsg({ type: 'error', text: 'Kullanıcı adı, e-posta ve ad-soyad zorunlu' });
    if (modalMode === 'add' && (!password || password.length < 6))
      return setMsg({ type: 'error', text: 'Şifre en az 6 karakter olmalı' });
    setSaving(true); setMsg(null);
    try {
      const body = {
        username: form.username.trim(),
        email: form.email.trim(),
        full_name: form.full_name.trim(),
        phone: form.phone || null,
        role: form.role,
        is_active: form.is_active,
        is_blocked: form.is_blocked,
        role_ids: form.role_ids,
        personnel_id: form.personnel_id || null,
        default_cash_register_id: form.default_cash_register_id || null,
        default_warehouse_id: form.default_warehouse_id || null,
        default_branch_id: form.default_branch_id || null,
        is_cashier: !!form.is_cashier,
        retail_allow_credit_card:   form.retail_allow_credit_card !== false,
        retail_allow_bank_transfer: form.retail_allow_bank_transfer !== false,
        retail_allow_voucher:       form.retail_allow_voucher !== false,
        retail_allow_on_account:    form.retail_allow_on_account !== false,
        retail_allow_price_edit:    form.retail_allow_price_edit !== false,
        period_ids: form.period_ids,
        visible_warehouse_ids: form.visible_warehouse_ids || [],
      };
      if (modalMode === 'add') {
        body.password = password;
        await api.post('/users', body);
      } else {
        await api.put('/users/' + selected.id, body);
        if (pwDirty && password) {
          await api.put('/users/' + selected.id + '/password', { password });
        }
      }
      await fetchAll();
      if (mode === 'close') closeModal();
      else if (mode === 'new') { setSelected(null); setForm({ username: '', email: '', full_name: '', phone: '', role: 'user', is_active: true, is_blocked: false, role_ids: [], period_ids: [] }); setPassword(''); setPwDirty(false); setModalMode('add'); setMsg(null); }
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Kaydedilemedi' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selected) return;
    if (selected.id === me?.id) return setMsg({ type: 'error', text: 'Kendi hesabınızı silemezsiniz' });
    if (!window.confirm(`${selected.full_name} kullanıcısı pasif yapılacak. Devam edilsin mi?`)) return;
    try {
      await api.delete('/users/' + selected.id);
      closeModal();
      fetchAll();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Silinemedi' });
    }
  };

  const handleHardDelete = async () => {
    if (!selected) return;
    if (selected.id === me?.id) return setMsg({ type: 'error', text: 'Kendi hesabınızı silemezsiniz' });
    if (!window.confirm(`${selected.full_name} kullanıcısı KALICI olarak silinecek. Bu işlem geri alınamaz!\n\nİlişkili kayıt varsa (fatura, log vb.) silinemez. Devam edilsin mi?`)) return;
    try {
      await api.delete('/users/' + selected.id + '?hard=true');
      closeModal();
      fetchAll();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Silinemedi' });
    }
  };

  const filtered = list.filter(u =>
    !search ||
    (u.username || '').toLowerCase().includes(search.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(search.toLowerCase()) ||
    (u.full_name || '').toLowerCase().includes(search.toLowerCase())
  );

  // Modal aksiyonları — footer'a taşındı (yeni Modal API)

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Kullanıcılar"
        count={filtered.length}
        unit="kayıt"
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Ad, e-posta, kullanıcı adı..."
        onRefresh={fetchAll}
        refreshing={loading}
        actions={canCreate ? <Button size="sm" onClick={openAdd}>+ Yeni Kullanıcı</Button> : null}
      />

      <FilterBar
        show={showFilterBar}
        onToggle={() => setShowFilterBar(s => !s)}
        values={filters}
        onChange={setFilters}
        fields={[
          { key: 'role', type: 'select', label: 'Rol',
            options: [
              { value: '', label: 'Tümü' },
              { value: 'admin', label: 'Admin' },
              { value: 'manager', label: 'Manager' },
              { value: 'user', label: 'Kullanıcı' },
            ] },
          { key: 'is_active', type: 'select', label: 'Durum',
            options: [{ value: '', label: 'Tümü' }, { value: 'yes', label: 'Aktif' }, { value: 'no', label: 'Pasif' }] },
          { key: 'is_blocked', type: 'select', label: 'Engelli',
            options: [{ value: '', label: 'Tümü' }, { value: 'yes', label: 'Bloke' }, { value: 'no', label: 'Bloke Değil' }] },
        ]}
      />

      <div className="stocks-table-wrap">
        <DataTable
          pageKey="users-list"
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText="Kullanıcı bulunamadı"
          data={filtered}
          rowKey="id"
          onRowClick={u => canEdit && openEdit(u)}
          columns={[
            { id: 'name', header: 'Kullanıcı', accessor: u => u.full_name + ' ' + u.username, sortable: true,
              cell: u => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {u.image_url
                    ? <img src={u.image_url} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}/>
                    : <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-tertiary)', flexShrink: 0 }}>{(u.full_name || u.username || '?').split(' ').map(s=>s[0]).join('').toUpperCase().slice(0,2)}</div>
                  }
                  <div><div style={{ fontWeight: 600 }}>{u.full_name}</div><div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{u.username}</div></div>
                </div>
              ) },
            { id: 'email', header: 'E-posta', accessor: 'email', sortable: true,
              cell: u => <span className="stock-secondary">{u.email}</span> },
            { id: 'roles', header: 'Roller', accessor: 'roles_text', sortable: true,
              cell: u => <span className="stock-secondary" style={{ fontSize: '0.78rem' }}>{u.roles_text || '—'}</span> },
            { id: 'role', header: 'Rol (legacy)', accessor: 'role', sortable: true, width: 120,
              cell: u => <span className="stock-badge" style={{ background: u.role === 'admin' ? 'rgba(220,38,38,0.1)' : 'rgba(99,102,241,0.1)', color: u.role === 'admin' ? '#dc2626' : 'var(--accent-primary)' }}>{u.role || '—'}</span> },
            { id: 'last_login', header: 'Son Giriş', accessor: 'last_login', sortable: true, width: 150,
              cell: u => <span className="stock-secondary" style={{ fontSize: '0.78rem' }}>{fmtDate(u.last_login)}</span> },
            { id: 'status', header: 'Durum', accessor: u => u.is_blocked ? 'Bloke' : (u.is_active ? 'Aktif' : 'Pasif'), sortable: true, width: 100,
              headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' },
              cell: u => u.is_blocked ? <span className="stock-badge passive" style={{ background: 'rgba(220,38,38,0.1)', color: '#dc2626' }}>Bloke</span>
                : <span className={'stock-badge ' + (u.is_active ? 'active' : 'passive')}>{u.is_active ? 'Aktif' : 'Pasif'}</span> },
          ]}
        />
      </div>

      {modalMode !== null && (
        <Modal
          icon={null}
          title={modalMode === 'add' ? 'Yeni Kullanıcı' : 'Kullanıcı Kartı'}
          size="lg"
          onClose={closeModal}
          footer={
            <>
              <div className="ui-modal-footer-left" style={{ display: 'flex', gap: 8 }}>
                {modalMode === 'edit' && canDelete && selected?.id !== me?.id && (
                  <>
                    <Button variant="danger" onClick={handleDelete}>🚫 Pasif Yap</Button>
                    <Button variant="ghost" onClick={handleHardDelete} style={{ color: 'var(--danger)' }}>🗑 Kalıcı Sil</Button>
                  </>
                )}
              </div>
              <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Button variant="ghost" onClick={closeModal}>{modalMode === 'add' ? 'Vazgeç' : 'İptal'}</Button>
                <CardSplitButton
                  primaryLabel="💾 Kaydet"
                  primaryAction={() => handleSave('close')}
                  loading={saving}
                  options={[
                    { label: 'Kaydet ve Yeni Aç', icon: '✚', onClick: () => handleSave('new') },
                    { label: 'Kaydet ve Kapat',  icon: '✓', onClick: () => handleSave('close') },
                  ]}
                />
              </div>
            </>
          }
        >
          {modalMode === 'edit' && (
            <CardHero
              icon="👨‍💻"
              code={selected?.username}
              title={selected?.full_name || form.full_name}
              subtitle={form.email}
              statusBadge={
                selected?.is_active === false
                  ? <CardStatusBadge preset="inactive" />
                  : <CardStatusBadge preset="active" />
              }
              kpis={[
                { label: 'Rol Sayısı', value: form.role_ids.length, color: 'primary' },
                selected?.last_login_at && {
                  label: 'Son Giriş', value: new Date(selected.last_login_at).toLocaleDateString('tr-TR'), color: 'info',
                },
              ].filter(Boolean)}
            />
          )}
          {msg && (
            <div style={{
              marginBottom: 12, padding: '9px 12px', borderRadius: 8, fontSize: '0.84rem',
              background: msg.type === 'error' ? 'var(--danger-bg)' : 'rgba(16,185,129,0.1)',
              color: msg.type === 'error' ? 'var(--danger)' : '#0d9488',
              border: '1px solid ' + (msg.type === 'error' ? 'rgba(239,68,68,0.25)' : 'rgba(16,185,129,0.25)'),
            }}>{msg.text}</div>
          )}

          <CardTabs
            items={[
              { key: 'general',  label: 'Genel',  icon: <TabIcon name="general"/> },
              { key: 'roles',    label: 'Roller', icon: <TabIcon name="shield"/>, badge: form.role_ids.length || null },
              { key: 'retail',   label: 'Perakende Satış Ayarları', icon: <TabIcon name="cash"/> },
              { key: 'warehouses', label: 'Depo Yetkileri', icon: <TabIcon name="package"/>, badge: (form.visible_warehouse_ids || []).length || null },
              { key: 'password', label: 'Şifre',  icon: <TabIcon name="key"/> },
              ...(modalMode === 'edit' ? [{ key: 'image', label: 'Resim', icon: <TabIcon name="image"/> }] : []),
            ]}
            active={activeTab}
            onChange={setActiveTab}
          />

          {activeTab === 'general' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              <FormField label="Kullanıcı Adı" required>
                <input value={form.username} onChange={e => tf('username', e.target.value)} maxLength={50}/>
              </FormField>
              <FormField label="E-posta" required>
                <input type="email" value={form.email} onChange={e => tf('email', e.target.value)} maxLength={100}/>
              </FormField>
              <FormField label="Ad Soyad" required fullWidth>
                <input value={form.full_name} onChange={e => tf('full_name', e.target.value)}/>
              </FormField>
              <FormField label="Telefon">
                <input value={form.phone || ''} onChange={e => tf('phone', e.target.value)}/>
              </FormField>
              <FormField label="Sistem Rolü (legacy)">
                <select value={form.role} onChange={e => tf('role', e.target.value)}>
                  <option value="admin">admin (tam yetki)</option>
                  <option value="manager">manager</option>
                  <option value="user">user</option>
                </select>
              </FormField>
              <FormField label="Durum">
                <select value={form.is_active ? '1' : '0'} onChange={e => tf('is_active', e.target.value === '1')} disabled={selected?.id === me?.id}>
                  <option value="1">Aktif</option>
                  <option value="0">Pasif</option>
                </select>
              </FormField>
              <FormField label="Bloke">
                <select value={form.is_blocked ? '1' : '0'} onChange={e => tf('is_blocked', e.target.value === '1')} disabled={selected?.id === me?.id}>
                  <option value="0">Hayır</option>
                  <option value="1">Bloke</option>
                </select>
              </FormField>

              <div style={{ gridColumn: '1/-1', marginTop: 8, paddingTop: 12, borderTop: '1px dashed var(--border-secondary)', fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                Bağlantılar (varsayılan)
              </div>
              <FormField label="Personel">
                <select value={form.personnel_id || ''} onChange={e => tf('personnel_id', e.target.value)}>
                  <option value="">— Seçiniz —</option>
                  {personnel.map(p => <option key={p.id} value={p.id}>{p.code ? p.code + ' - ' : ''}{p.full_name || (p.first_name + ' ' + p.last_name)}</option>)}
                </select>
              </FormField>
              <FormField label="Varsayılan Kasa">
                <select value={form.default_cash_register_id || ''} onChange={e => tf('default_cash_register_id', e.target.value)}>
                  <option value="">— Seçiniz —</option>
                  {cashRegisters.map(c => <option key={c.id} value={c.id}>{c.register_name} {c.currency_code ? '(' + c.currency_code + ')' : ''}</option>)}
                </select>
              </FormField>
              <FormField label="Varsayılan Depo">
                <select value={form.default_warehouse_id || ''} onChange={e => tf('default_warehouse_id', e.target.value)}>
                  <option value="">— Seçiniz —</option>
                  {warehouses.map(w => <option key={w.id} value={w.id}>{w.code ? w.code + ' - ' : ''}{w.name}</option>)}
                </select>
              </FormField>
              <FormField label="Varsayılan Şube">
                <select value={form.default_branch_id || ''} onChange={e => tf('default_branch_id', e.target.value)}>
                  <option value="">— Seçiniz —</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.code ? b.code + ' - ' : ''}{b.name}</option>)}
                </select>
              </FormField>

              <div style={{ gridColumn: '1/-1', marginTop: 8, padding: '12px 14px', borderRadius: 8,
                background: form.is_cashier ? 'rgba(124,58,237,0.08)' : 'var(--bg-secondary)',
                border: '1px solid ' + (form.is_cashier ? 'rgba(124,58,237,0.3)' : 'var(--border-secondary)') }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!form.is_cashier} onChange={e => tf('is_cashier', e.target.checked)} style={{ marginTop: 3 }} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>🏪 Mağaza / Kasiyer Modu</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginTop: 4 }}>
                      Bu kullanıcı giriş yaptığında <strong>doğrudan Perakende Mağaza ekranı</strong> açılır. Diğer ERP menüleri gizlenir. Varsayılan kasa ve depo otomatik kullanılır.
                    </div>
                    {form.is_cashier && (!form.default_cash_register_id || !form.default_warehouse_id) && (
                      <div style={{ fontSize: '0.78rem', color: 'var(--warning)', marginTop: 6 }}>
                        ⚠ Mağaza modu için <strong>Varsayılan Kasa</strong> ve <strong>Varsayılan Depo</strong> seçili olmalı.
                      </div>
                    )}
                  </div>
                </label>
              </div>

              <div style={{ gridColumn: '1/-1', marginTop: 8, paddingTop: 12, borderTop: '1px dashed var(--border-secondary)', fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                İzin Verilen Çalışma Dönemleri
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                {form.role === 'admin' ? (
                  <div style={{ padding: 10, background: 'rgba(220,38,38,0.05)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 8, fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                    <strong>admin</strong> rolündeki kullanıcılar tüm dönemleri görür/seçer (kısıtlama uygulanmaz).
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginBottom: 8 }}>
                      Bu kullanıcı sadece seçilen dönemleri görür ve seçebilir. Hiç seçim yapılmazsa kısıtlama yok — tüm açık dönemleri görür.
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 6 }}>
                      {periods.length === 0 && <div style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>Tanımlı dönem yok</div>}
                      {periods.map(p => (
                        <label key={p.id} style={{ display: 'flex', gap: 8, padding: 8, border: '1px solid var(--border-secondary)', borderRadius: 6, cursor: 'pointer', background: form.period_ids.includes(p.id) ? 'rgba(99,102,241,0.08)' : 'transparent' }}>
                          <input type="checkbox" checked={form.period_ids.includes(p.id)} onChange={() => togglePeriod(p.id)}/>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: '0.82rem' }}>{p.code}</div>
                            <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>{p.name} {p.status === 'closed' ? '(Kapalı)' : ''}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {activeTab === 'roles' && (
            <div style={{ padding: '8px 0' }}>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-tertiary)', marginBottom: 12 }}>
                Kullanıcının atanacağı rolleri seçin. Birden fazla seçilebilir; etkin izinler tüm rollerin birleşimidir.
                {form.role === 'admin' && <span style={{ color: '#dc2626', display: 'block', marginTop: 4 }}>Not: <b>admin</b> sistem rolü her durumda tam yetkilidir, atanan roller etkili olmaz.</span>}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
                {roles.length === 0 && <div style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>Tanımlı rol yok</div>}
                {roles.map(r => (
                  <label key={r.id} style={{ display: 'flex', gap: 8, padding: 10, border: '1px solid var(--border-secondary)', borderRadius: 8, cursor: 'pointer', background: form.role_ids.includes(r.id) ? 'rgba(99,102,241,0.08)' : 'transparent' }}>
                    <input type="checkbox" checked={form.role_ids.includes(r.id)} onChange={() => toggleRole(r.id)} style={{ marginTop: 3 }}/>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{r.name} {r.is_system && <span style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', marginLeft: 6 }}>(sistem)</span>}</div>
                      {r.description && <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: 2 }}>{r.description}</div>}
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', marginTop: 4 }}>{r.user_count || 0} kullanıcı</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'retail' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 4 }}>
                Bu kullanıcı perakende satış ekranında <strong>Nakit</strong> dışında hangi ödeme yöntemlerini görebileceğini buradan kısıtlayın.
                İşaretli olmayan yöntem PoS ödeme bölmesine eklenemez.
              </div>
              {[
                { k: 'retail_allow_credit_card',   label: '💳 Kredi Kartı Tahsilatı',  hint: 'POS tanımı seçip taksitli/peşin kart tahsilatı yapabilir' },
                { k: 'retail_allow_bank_transfer', label: '🏦 Banka Havalesi',          hint: 'Müşteri havale ile öderse banka hesabı satırı eklenebilir' },
                { k: 'retail_allow_voucher',       label: '🎫 Alış-Veriş Çeki',         hint: 'Müşterinin elindeki indirim/hediye çeklerini ödeme olarak kullanabilir' },
                { k: 'retail_allow_on_account',    label: '📒 Cari Açık Hesap',         hint: 'Satışın bir kısmı / tamamı müşteri carisine yazılabilir (sonra tahsil edilmek üzere)' },
                { k: 'retail_allow_price_edit',    label: '💰 Manuel Fiyat Değişikliği', hint: 'PoS\'ta satır birim fiyatını elle değiştirebilir. Kapalıysa keypad\'in "Fiyat" butonu pasif kalır, sadece fiyat tanımındaki fiyat geçerli olur.' },
              ].map(opt => (
                <label key={opt.k} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-secondary)', background: form[opt.k] ? 'var(--accent-glow)' : 'var(--bg-secondary)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form[opt.k] !== false} onChange={e => tf(opt.k, e.target.checked)} style={{ marginTop: 3, width: 18, height: 18 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{opt.label}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 3 }}>{opt.hint}</div>
                  </div>
                </label>
              ))}
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', padding: 8 }}>
                <strong>Nakit</strong> her zaman açıktır ve kapatılamaz.
              </div>
            </div>
          )}

          {activeTab === 'warehouses' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                Bu kullanıcının <strong>belge, rapor ve analizlerde</strong> görebileceği/kullanabileceği depoları seçin.
                <br/><strong style={{ color: 'var(--success)' }}>Hiçbir seçim yapılmazsa</strong> kullanıcı <strong>TÜM depoları</strong> görür (varsayılan).
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <Button size="sm" variant="ghost" onClick={() => tf('visible_warehouse_ids', warehouses.map(w => w.id))}>Tümünü Seç</Button>
                <Button size="sm" variant="ghost" onClick={() => tf('visible_warehouse_ids', [])}>Temizle (Hepsi Görünür)</Button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 8, maxHeight: 380, overflowY: 'auto', border: '1px solid var(--border-secondary)', borderRadius: 8, padding: 10 }}>
                {warehouses.length === 0 && <div style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>Aktif depo yok</div>}
                {warehouses.map(w => {
                  const ids = form.visible_warehouse_ids || [];
                  const checked = ids.includes(w.id);
                  return (
                    <label key={w.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border-secondary)', background: checked ? 'var(--accent-glow)' : 'var(--bg-secondary)', cursor: 'pointer' }}>
                      <input type="checkbox" checked={checked}
                        onChange={(e) => {
                          const next = e.target.checked ? [...ids, w.id] : ids.filter(x => x !== w.id);
                          tf('visible_warehouse_ids', next);
                        }}
                        style={{ width: 18, height: 18 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{w.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>
                          {w.code}{w.warehouse_type ? ' · ' + w.warehouse_type : ''}{w.branch_name ? ' · 🏢 ' + w.branch_name : ''}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                Seçili: <strong>{(form.visible_warehouse_ids || []).length}</strong> / {warehouses.length} depo
                {(form.visible_warehouse_ids || []).length === 0 && <span style={{ color: 'var(--success)', marginLeft: 6 }}>→ Hepsini görür</span>}
              </div>
            </div>
          )}

          {activeTab === 'password' && (
            <FormField
              label={modalMode === 'add' ? 'Şifre' : 'Yeni Şifre'}
              required={modalMode === 'add'}
              hint="En az 6 karakter"
              fullWidth
            >
              <input
                type="password"
                value={password}
                onChange={e => { setPassword(e.target.value); setPwDirty(true); }}
                placeholder={modalMode === 'edit' ? 'Boş bırakılırsa değişmez' : ''}
                autoComplete="new-password"
              />
            </FormField>
          )}

          {activeTab === 'image' && modalMode === 'edit' && (
            <FormField label="Profil Resmi" fullWidth>
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{
                  width: 140, height: 140, borderRadius: 8,
                  border: '1px solid var(--border-secondary)',
                  background: 'var(--bg-input)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  overflow: 'hidden', flexShrink: 0,
                }}>
                  {form.image_url ? (
                    <img src={form.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}/>
                  ) : (
                    <span style={{ color: 'var(--text-tertiary)', fontSize: '0.78rem' }}>Resim yok</span>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <input
                    ref={imgInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file || !selected?.id) return;
                      setImgUploading(true);
                      try {
                        const fd = new FormData();
                        fd.append('image', file);
                        const res = await api.post('/users/' + selected.id + '/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                        if (res.data.success) {
                          tf('image_url', res.data.image_url);
                          setMsg({ type: 'success', text: 'Resim yüklendi' });
                        }
                      } catch (err) { setMsg({ type: 'error', text: 'Resim yüklenemedi' }); }
                      finally { setImgUploading(false); if (imgInputRef.current) imgInputRef.current.value = ''; }
                    }}
                  />
                  <span style={{ marginRight: 6, display: 'inline-block' }}>
                    <Button size="sm" loading={imgUploading} onClick={() => imgInputRef.current?.click()}>
                      {form.image_url ? 'Resmi Değiştir' : '+ Resim Yükle'}
                    </Button>
                  </span>
                  {form.image_url && (
                    <Button variant="danger" size="sm" onClick={async () => {
                      if (!window.confirm('Resmi silmek istiyor musunuz?')) return;
                      try {
                        await api.delete('/users/' + selected.id + '/image');
                        tf('image_url', null);
                        setMsg({ type: 'success', text: 'Resim silindi' });
                      } catch (err) { setMsg({ type: 'error', text: 'Resim silinemedi' }); }
                    }}>Sil</Button>
                  )}
                  <div style={{ marginTop: 8, fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>
                    JPG/PNG · max 5 MB
                  </div>
                </div>
              </div>
            </FormField>
          )}
        </Modal>
      )}
    </div>
  );
}
